/**
 * Profile Service
 * Handles user profile management operations
 */

import apiClient from "../utils/apiClient";
import logger from "../utils/logger";

class ProfileService {
  constructor() {
    this.baseUrl = "/api/casino/profile";
    this.serviceName = "ProfileService";
  }

  /**
   * Get the authenticated player's own profile identity fields.
   *
   * Reads from the `players` record server-side (the same source of truth that
   * admin edits and KYC/Sumsub use), NOT from Supabase auth user_metadata. This
   * is what keeps the profile name/DOB/address correct after an admin edit.
   * @returns {Promise<Object>} Profile view-model (firstName, lastName, dateOfBirth, country, state, city, address, zipCode, username, createdAt)
   */
  async getProfile() {
    try {
      const response = await apiClient.get(this.baseUrl);
      return response.data?.profile || null;
    } catch (error) {
      logger.error(`[${this.serviceName}] Error fetching profile:`, error);
      throw this._handleError(error, "Failed to fetch profile");
    }
  }

  /**
   * Check if username is available
   * @param {string} username - Username to check
   * @returns {Promise<Object>} Availability result
   */
  async checkUsernameAvailability(username) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/check-username`, {
        params: { username },
      });
      return response.data;
    } catch (error) {
      logger.error(
        `[${this.serviceName}] Error checking username availability:`,
        error,
      );
      throw this._handleError(error, "Failed to check username availability");
    }
  }

  /**
   * Update username
   * @param {string} username - New username
   * @returns {Promise<Object>} Update result
   */
  async updateUsername(username) {
    try {
      const response = await apiClient.put(`${this.baseUrl}/username`, {
        username,
      });

      return response.data;
    } catch (error) {
      logger.error(`[${this.serviceName}] Error updating username:`, error);
      throw this._handleError(error, "Failed to update username");
    }
  }

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Change result
   */
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await apiClient.put(`${this.baseUrl}/password`, {
        currentPassword,
        newPassword,
      });

      return response.data;
    } catch (error) {
      logger.error(`[${this.serviceName}] Error changing password:`, error);
      throw this._handleError(error, "Failed to change password");
    }
  }

  /**
   * Get player transaction history
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Transaction history
   */
  async getTransactionHistory(options = {}) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/transactions`, {
        params: options,
      });
      return response.data;
    } catch (error) {
      logger.error(
        `[${this.serviceName}] Error fetching transaction history:`,
        error,
      );
      throw this._handleError(error, "Failed to fetch transaction history");
    }
  }

  /**
   * Get player game history
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Game history
   */
  async getGameHistory(options = {}) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/game-history`, {
        params: options,
      });
      return response.data;
    } catch (error) {
      logger.error(`[${this.serviceName}] Error fetching game history:`, error);
      throw this._handleError(error, "Failed to fetch game history");
    }
  }

  /**
   * Get recently played games (unique games in order of last played)
   * @param {number} limit - Number of games to return
   * @returns {Promise<Array>} Recently played games
   */
  async getRecentlyPlayedGames(limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/recently-played`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      logger.error(
        `[${this.serviceName}] Error fetching recently played games:`,
        error,
      );
      throw this._handleError(error, "Failed to fetch recently played games");
    }
  }

  /**
   * Request self-exclusion
   * @param {string} duration - Exclusion duration (7_days, 30_days, 1_year, permanent)
   * @param {string} password - User's password for confirmation
   * @returns {Promise<Object>} Self-exclusion result
   */
  async selfExclude(duration, password) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/self-exclude`, {
        duration,
        confirmPassword: password,
      });
      return response.data;
    } catch (error) {
      logger.error(
        `[${this.serviceName}] Error requesting self-exclusion:`,
        error,
      );
      throw this._handleError(error, "Failed to apply self-exclusion");
    }
  }

  /**
   * Handle API errors with user-friendly messages
   * @param {Error} error - Original error
   * @param {string} defaultMessage - Default error message
   * @returns {Error} Processed error
   */
  _handleError(error, defaultMessage) {
    if (error.response) {
      const { status, data } = error.response;

      // Handle specific error cases
      switch (status) {
        case 400:
          return new Error(data.message || "Invalid request data");
        case 401:
          return new Error("Authentication required");
        case 403:
          // Surface the backend's reason if present — endpoints like
          // /self-exclude raise ForbiddenError with specific messages
          // ("Incorrect password", "Account is already suspended", etc.)
          // that we used to swallow as a generic "Access denied".
          return new Error(data?.message || data?.error || "Access denied");
        case 404:
          return new Error("Resource not found");
        case 409:
          return new Error(data.message || "Username already taken");
        case 422:
          return new Error(data.error || data.message || "Validation failed");
        case 429:
          return new Error("Too many requests. Please try again later.");
        case 500:
          return new Error("Server error. Please try again later.");
        default:
          return new Error(data.message || defaultMessage);
      }
    }

    if (error.request) {
      return new Error("Network error. Please check your connection.");
    }

    return new Error(error.message || defaultMessage);
  }
}

// Create and export singleton instance
const profileService = new ProfileService();
export default profileService;
